import { useMemo } from 'react';

export type MapElement = {
  id: string;
  parentId: string | null;
  type: string;
  name: string;
  identifier: string | null;
  status: string;
  areaId?: string | null;
  area?: { id: string; name: string; zone: { code: string; name: string } } | null;
};

/**
 * A sketch of how the network is actually wired.
 *
 * The tree view answers "what is under this element". It does not answer the
 * question a dispatcher asks at 8am — *where* is the break, and who is behind
 * it — because a nested list of names hides the shape. This draws the shape:
 * POPs on the left, then spans, FATs and customer boxes flowing right, each
 * link coloured by whether it is carrying traffic.
 *
 * Deliberately a schematic, not a geographic map. GoSpeedNet has no survey
 * coordinates for its poles, and a diagram that invents positions would be
 * read as if it knew where things were. Left-to-right by depth is honest about
 * what it is: connection order, not distance.
 *
 * Hand-drawn SVG rather than a graph library — this is four tiers and a few
 * dozen nodes, and the layout rule fits on a screen.
 */

/**
 * Column headings, by depth in the tree rather than by the `type` field.
 *
 * Depth is the real topology: if somebody records a FAT hanging off another
 * FAT, the diagram should show four hops and not quietly redraw it as three.
 */
const TIER_LABEL = ['POP', 'Backbone / span', 'FAT', 'Customer box'];

const STATUS_COLOUR: Record<string, string> = {
  up: 'var(--map-up)',
  degraded: 'var(--map-degraded)',
  down: 'var(--map-down)',
  decommissioned: 'var(--map-off)',
};

const COLUMN_WIDTH = 210;
const ROW_HEIGHT = 34;
const NODE_RADIUS = 6;
const PADDING = { top: 44, left: 24, bottom: 20 };

export function NetworkMap({
  elements,
  selectedId,
  onSelect,
}: {
  elements: MapElement[];
  selectedId?: string | null;
  onSelect?: (element: MapElement) => void;
}) {
  const layout = useMemo(() => {
    const childrenOf = new Map<string | null, MapElement[]>();
    for (const element of elements) {
      const key = element.parentId;
      childrenOf.set(key, [...(childrenOf.get(key) ?? []), element]);
    }

    /*
     * Leaves get consecutive rows; every parent then sits at the midpoint of
     * its own children. That is what keeps a POP level with the block of work
     * hanging off it instead of drifting to the top while its FATs march down
     * the page — the columns advance at different rates, so packing each tier
     * independently would cross almost every line on the diagram.
     */
    const position = new Map<string, { x: number; y: number }>();
    let nextLeafRow = 0;
    let deepestTier = 0;

    const place = (element: MapElement, tier: number): number => {
      deepestTier = Math.max(deepestTier, tier);
      const children = childrenOf.get(element.id) ?? [];

      const row = children.length
        ? children.reduce((sum, child) => sum + place(child, tier + 1), 0) / children.length
        : nextLeafRow++;

      position.set(element.id, {
        x: PADDING.left + tier * COLUMN_WIDTH,
        y: PADDING.top + row * ROW_HEIGHT,
      });
      return row;
    };

    for (const root of childrenOf.get(null) ?? []) place(root, 0);
    // Anything whose parent was not in the payload would otherwise be invisible.
    for (const orphan of elements.filter((e) => !position.has(e.id))) place(orphan, 0);

    return {
      position,
      width: PADDING.left * 2 + (deepestTier + 1) * COLUMN_WIDTH,
      height: PADDING.top + Math.max(nextLeafRow, 1) * ROW_HEIGHT + PADDING.bottom,
      usedTiers: deepestTier + 1,
    };
  }, [elements]);

  if (elements.length === 0) {
    return <p className="text-sm text-muted-foreground">No network elements recorded yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        width={layout.width}
        height={layout.height}
        role="img"
        aria-label="Sketch of the network, from points of presence out to customer boxes"
        className="min-w-full"
        style={
          {
            // Defined here rather than in the stylesheet so the two themes stay
            // in one place with the shapes that use them.
            '--map-up': '#0f9d58',
            '--map-degraded': '#e8a33d',
            '--map-down': '#d64545',
            '--map-off': '#9aa0a6',
          } as React.CSSProperties
        }
      >
        {Array.from({ length: layout.usedTiers }, (_, tier) => (
          <text
            key={tier}
            x={PADDING.left + tier * COLUMN_WIDTH}
            y={20}
            className="fill-muted-foreground"
            style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}
          >
            {TIER_LABEL[tier] ?? `Tier ${tier + 1}`}
          </text>
        ))}

        {/* Links first, so nodes sit on top of them. */}
        <g fill="none" strokeWidth={1.5}>
          {elements.map((element) => {
            if (!element.parentId) return null;
            const from = layout.position.get(element.parentId);
            const to = layout.position.get(element.id);
            if (!from || !to) return null;

            // A link is only as good as the worse end of it: a healthy FAT
            // behind a cut span is not reachable, and drawing that leg green
            // would be the single most misleading thing on the diagram.
            const parent = elements.find((e) => e.id === element.parentId);
            const worst =
              parent?.status === 'down' || element.status === 'down'
                ? 'down'
                : parent?.status === 'degraded' || element.status === 'degraded'
                  ? 'degraded'
                  : element.status;

            const midX = (from.x + to.x) / 2;
            return (
              <path
                key={`${element.parentId}-${element.id}`}
                d={`M ${from.x} ${from.y} C ${midX} ${from.y}, ${midX} ${to.y}, ${to.x} ${to.y}`}
                stroke={STATUS_COLOUR[worst] ?? 'var(--map-off)'}
                strokeOpacity={worst === 'down' ? 0.9 : 0.45}
                strokeDasharray={worst === 'down' ? '4 3' : undefined}
              />
            );
          })}
        </g>

        {elements.map((element) => {
          const at = layout.position.get(element.id);
          if (!at) return null;
          const selected = element.id === selectedId;
          const colour = STATUS_COLOUR[element.status] ?? 'var(--map-off)';

          return (
            <g
              key={element.id}
              transform={`translate(${at.x}, ${at.y})`}
              onClick={() => onSelect?.(element)}
              className={onSelect ? 'cursor-pointer' : undefined}
              tabIndex={onSelect ? 0 : undefined}
              role={onSelect ? 'button' : undefined}
              /*
               * Named twice, deliberately. <title> is what shows on hover, and
               * is the SVG accessible name — but a node carrying role="button"
               * is announced inconsistently from a <title> child alone, and an
               * unnamed button is a square that does something unknowable.
               */
              aria-label={
                onSelect
                  ? `${element.name}${element.area ? ` in ${element.area.name}` : ''} — ${element.status}`
                  : undefined
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect?.(element);
                }
              }}
            >
              <title>
                {element.name}
                {element.area ? ` — ${element.area.name} (${element.area.zone.code})` : ''} —{' '}
                {element.status}
              </title>

              {selected && <circle r={NODE_RADIUS + 4} fill={colour} fillOpacity={0.2} />}
              <circle
                r={NODE_RADIUS}
                fill={colour}
                stroke="var(--card, #fff)"
                strokeWidth={selected ? 2 : 1}
              />
              {/* A down element gets a ring as well as a colour, so it is not
                  the only thing distinguishing it for a colour-blind reader. */}
              {element.status === 'down' && (
                <circle r={NODE_RADIUS + 3} fill="none" stroke={colour} strokeWidth={1.5} />
              )}

              <text
                x={NODE_RADIUS + 6}
                y={4}
                className="fill-foreground"
                style={{ fontSize: 11, fontWeight: selected ? 600 : 400 }}
              >
                {element.name.length > 22 ? `${element.name.slice(0, 21)}…` : element.name}
              </text>
              {element.area && element.type !== 'ont' && (
                <text
                  x={NODE_RADIUS + 6}
                  y={15}
                  className="fill-muted-foreground"
                  style={{ fontSize: 9 }}
                >
                  {element.area.name}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
